// Pantau Produksi Shopee per PIC — dropdown yang dapat dicari, ringkasan PIC, peran per tahap, Riwayat Selesai Shopee.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronDown, Search, X, UserRound } from "lucide-react";
import "./shopee_pic_filter.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => iso ? new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) + " WIB" : "-";
const STATE_LABEL = { dikerjakan: "sedang", menunggu: "menunggu", riwayat: "riwayat" };

export function PicPicker({ options, pic, setPic, picMode, setPicMode, unassigned, setUnassigned, historyOnly = false, testid = "monitor-pic" }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const list = (historyOnly ? options : (options?.[picMode === "history" ? "history" : "current"])) || [];
  const shown = list.filter(o => o.name.toLowerCase().includes(term.trim().toLowerCase()));
  const choose = (name, un = false) => { setPic(name); if (setUnassigned) setUnassigned(un); setOpen(false); setTerm(""); };
  const label = pic || (unassigned ? "Belum Ditugaskan" : "Semua Operator");
  return <div className="spf" ref={ref} data-testid={`${testid}-picker`}>
    <button type="button" className={`spf-trigger ${pic || unassigned ? "on" : ""}`} onClick={() => setOpen(v => !v)} aria-expanded={open}
      data-testid={`${testid}-toggle`}><UserRound size={14}/> <span>Operator — <b>{label}</b></span><ChevronDown size={14}/></button>
    {(pic || unassigned) && <button type="button" className="spf-clear" onClick={() => choose("")} aria-label="Hapus filter Operator"
      data-testid={`${testid}-clear`}><X size={13}/></button>}
    {open && <div className="spf-pop" role="listbox">
      {!historyOnly && <div className="spf-mode" role="group" aria-label="Mode Operator">
        <button type="button" className={picMode !== "history" ? "on" : ""} onClick={() => setPicMode("current")} data-testid={`${testid}-mode-current`}>Saat ini menangani</button>
        <button type="button" className={picMode === "history" ? "on" : ""} onClick={() => setPicMode("history")} data-testid={`${testid}-mode-history`}>Pernah menangani</button>
      </div>}
      <label className="spf-search"><Search size={13}/><input autoFocus value={term} onChange={e => setTerm(e.target.value)} placeholder="Cari nama operator…"
        data-testid={`${testid}-search`}/></label>
      <ul>
        <li><button type="button" className={!pic && !unassigned ? "on" : ""} onClick={() => choose("")} data-testid={`${testid}-opt-all`}>Semua Operator</button></li>
        {setUnassigned && <li><button type="button" className={unassigned ? "on" : ""} onClick={() => choose("", true)} data-testid={`${testid}-opt-unassigned`}>Belum Ditugaskan</button></li>}
        {shown.map(o => <li key={o.name}><button type="button" className={pic === o.name ? "on" : ""} onClick={() => choose(o.name)}
          data-testid={`${testid}-opt-${o.name.replace(/\W+/g, "-").toLowerCase()}`}>
          <span>{o.name}<small>{o.divisions.join(" · ")}</small></span><em>{o.count}</em></button></li>)}
        {!shown.length && <li className="spf-empty">Tidak ada petugas{term ? ` "${term}"` : ""} pada cakupan ini</li>}
      </ul>
    </div>}
  </div>;
}

export function PicSummary({ s, picMode, status, setStatus }) {
  if (!s) return null;
  const tiles = [["Pesanan aktif", s.total, "all"], ["Sedang dikerjakan", s.dikerjakan], ["Menunggu", s.menunggu],
    ["Terlambat", s.terlambat, "overdue"], ["Hari ini / ≤24 jam", s.mendekati]];
  if (picMode === "history") tiles.push(["Sudah lewat tahapnya", s.riwayat]);
  return <div className="spf-sum" data-testid="monitor-pic-summary">
    <span className="spf-sum-title">{s.pic}{picMode === "history" ? " · pernah menangani" : " · saat ini"}</span>
    {tiles.map(([l, v, st]) => <button type="button" key={l} disabled={!st} className={st && status === st ? "on" : ""} onClick={() => st && setStatus(st)}
      data-testid={`monitor-pic-sum-${l.replace(/\W+/g, "-").toLowerCase()}`}><b>{v}</b><small>{l}</small></button>)}
  </div>;
}

export function PicRolesLine({ o, pic }) {
  const roles = o.pic_roles || [];
  if (!roles.length && !o.last_activity_at) return null;
  const pl = (pic || "").toLowerCase();
  const mine = pl ? roles.filter(r => r.name.toLowerCase() === pl) : [];
  const hist = roles.filter(r => r.state === "riwayat");
  return <div className="spf-roles" data-testid={`monitor-pic-roles-${o.order_number}`}>
    {mine.length > 0 && <p><span>Peran {pic}</span><b>{mine.map(r => `${r.division}: ${r.label} (${STATE_LABEL[r.state]})`).join(" · ")}</b></p>}
    {hist.length > 0 && <p><span>Riwayat Operator</span>{hist.map(r => `${r.label}: ${r.name}`).join(" · ")}</p>}
    {o.last_activity_at && <p><span>Aktivitas terakhir</span>{fmt(o.last_activity_at)}</p>}
  </div>;
}

const TASK_ST = { assigned: "menunggu", in_progress: "dikerjakan", done: "selesai" };
export function BordirTeamLine({ o }) {
  const b = o.bordir_team;
  if (!b) return null;
  return <div className="spf-roles" data-testid={`monitor-bordir-team-${o.order_number}`}>
    <p><span>Tim Bordir</span><b>Utama: {b.main || "-"} · {b.open ? `${b.open} bagian belum selesai` : "semua bagian selesai"}</b></p>
    {b.tasks.map((t, i) => <p key={i} data-testid={`monitor-bordir-task-${o.order_number}-${i}`}><span>{t.operator}{t.is_rework ? " (perbaikan)" : ""}</span>
      {t.positions} · {t.pcs_done}/{t.pcs_assigned} pcs · {t.units_done} satuan titik · {TASK_ST[t.status] || t.status}</p>)}
  </div>;
}

export function ShopeeHistoryPanel({ division }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");
  const [pic, setPic] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { const t = setTimeout(() => setQ(query.trim()), 300); return () => clearTimeout(t); }, [query]);
  useEffect(() => { setPage(1); }, [q, pic, division]);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    axios.get(`${API}/production/monitoring/shopee-history`, { withCredentials: true, params: { q, pic, division, page }, timeout: 20000 })
      .then(r => { if (alive) { setData(r.data); setErr(""); } })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || "Gagal memuat Riwayat Selesai Shopee."); });
    return () => { alive = false; };
  }, [open, q, pic, division, page]);
  return <section className="spf-hist" data-testid="shopee-history-panel">
    <button type="button" className="spf-hist-head" onClick={() => setOpen(v => !v)} aria-expanded={open} data-testid="shopee-history-toggle">
      Riwayat Selesai Shopee <small>baca-saja · sudah difinalisasi Shella / dikirim Shopee</small><span aria-hidden="true">{open ? "▲" : "▼"}</span></button>
    {open && <div className="spf-hist-body">
      <div className="spf-hist-tools">
        <label className="spf-search"><Search size={13}/><input type="search" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Cari nomor pesanan, pelanggan, produk…" data-testid="shopee-history-search"/></label>
        <PicPicker options={data?.pic_options || []} pic={pic} setPic={setPic} historyOnly testid="shopee-history-pic"/>
      </div>
      {err && <div className="spf-err" role="alert">{err}</div>}
      {!data ? <p className="spf-empty">Memuat…</p> : <>
        <p className="spf-count" data-testid="shopee-history-total">{data.total} pesanan{pic ? ` dikerjakan ${pic}` : ""}{division ? ` · divisi ${division}` : ""}</p>
        <ul className="spf-hist-list">{data.items.map(r => <li key={r.key} data-testid={`shopee-history-row-${r.order_number}`}>
          <div><b>{r.order_number}</b> · {r.customer_name}<small>{r.product}{r.process ? ` · ${r.process}` : ""}</small></div>
          <div><span className="spf-st">{r.status}</span><small>{r.finalized_at ? `Shella: ${r.finalized_by || "-"} · ${fmt(r.finalized_at)}` : `Aktivitas terakhir ${fmt(r.last_activity_at)}`}</small></div>
          <small className="spf-hist-pics">{r.pic_roles.map(x => `${x.label}: ${x.name}`).join(" · ") || "Petugas belum tercatat"}</small>
        </li>)}</ul>
        {data.pages > 1 && <div className="spf-pager">
          <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="shopee-history-prev">‹</button>
          <span>Halaman {data.page}/{data.pages}</span>
          <button type="button" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="shopee-history-next">›</button>
        </div>}
      </>}
    </div>}
  </section>;
}
